import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiStar } from 'react-icons/fi';

const RTOContainer = styled.section`
  background: white;
  padding: 4rem 0;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 3rem 1.5rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 2.5rem 1rem;
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const RTOContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
    padding: 0 1.5rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    padding: 0 1rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
    padding: 0;
  }
`;

const RTOLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  position: relative;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 500px;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    width: 450px;
    height: 350px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    width: 400px;
    height: 320px;
  }

  @media (max-width: 768px) {
    width: 350px;
    height: 300px;
  }
`;

const CertificationBadge = styled.img`
  width: 150px;
  height: 150px;
  position: absolute;
  z-index: 3;
  transition: all 0.6s ease;
  transform: ${props => {
    if (!props.scrollProgress) return 'rotate(0deg)';
    // Straighten when section is in middle (progress around 0.5)
    const rotation = props.scrollProgress < 0.4 ? props.scrollProgress * 180 : 
                    props.scrollProgress > 0.6 ? (1 - props.scrollProgress) * 180 + 180 : 
                    0;
    return `rotate(${rotation}deg)`;
  }};
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.2));

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    width: 140px;
    height: 140px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    width: 130px;
    height: 130px;
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
  }
`;

// First qualification image - positioned as background
const BackgroundImage = styled.div`
  width: 400px;
  height: 300px;
  background: url('/rto-partner-qualification-1.png') center/cover;
  border-radius: 20px;
  position: absolute;
  top: -10px;
  left: -20px;
  filter: ${props => props.inView ? 'blur(0px)' : 'blur(12px)'};
  opacity: ${props => props.inView ? '1' : '0.2'};
  transition: all 1s ease;
  z-index: 1;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    width: 380px;
    height: 280px;
    top: -15px;
    left: -20px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    width: 340px;
    height: 250px;
    top: -12px;
    left: -18px;
  }

  @media (max-width: 768px) {
    width: 280px;
    height: 200px;
    top: -10px;
    left: -15px;
  }
`;

// Second qualification image - positioned as foreground
const GuaranteedImage = styled.div`
  width: 350px;
  height: 250px;
  background: url('/rto-partner-qualification-2.png') center/cover;
  border-radius: 20px;
  position: absolute;
  bottom: -20px;
  right: -30px;
  filter: ${props => props.inView ? 'blur(0px)' : 'blur(12px)'};
  opacity: ${props => props.inView ? '1' : '0.2'};
  transition: all 1s ease 0.4s;
  z-index: 2;
  transform: ${props => props.inView ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)'};
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    width: 320px;
    height: 230px;
    bottom: -20px;
    right: -25px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    width: 290px;
    height: 210px;
    bottom: -18px;
    right: -22px;
  }

  @media (max-width: 768px) {
    width: 250px;
    height: 180px;
    bottom: -15px;
    right: -20px;
  }
`;

const CertifiedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Shadows Into Light', cursive;
  font-size: 1.8rem;
  font-weight: 600;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1rem;

  svg {
    color: ${props => props.theme.colors.secondary};
    font-size: 2rem;
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    font-size: 1.7rem;
    
    svg {
      font-size: 1.9rem;
    }
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    font-size: 1.6rem;
    
    svg {
      font-size: 1.8rem;
    }
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    
    svg {
      font-size: 1.7rem;
    }
  }
`;

const RTORight = styled.div`
  h2 {
    color: ${props => props.theme.colors.heading};
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;

    /* iPad Portrait */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
      font-size: 2.3rem;
    }

    /* iPad Landscape */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
      font-size: 2.1rem;
    }

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  p {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 1.1rem;
    line-height: 1.7;
    margin-bottom: 2rem;

    /* iPad Portrait */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
      font-size: 1.1rem;
    }

    /* iPad Landscape */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
      font-size: 1.05rem;
    }

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const ExploreButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-2px);
  }

  svg {
    font-size: 1.25rem;
  }
`;

const RTOSection = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const sectionHeight = rect.height;
        
        // Calculate if section is in view
        const isInView = rect.top < windowHeight && rect.bottom > 0;
        setInView(isInView);
        
        // Calculate scroll progress for rotation
        if (isInView) {
          // Progress from 0 to 1 as section moves through viewport
          const progress = Math.min(Math.max((windowHeight - rect.top) / (windowHeight + sectionHeight), 0), 1);
          setScrollProgress(progress);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <RTOContainer ref={sectionRef}>
      <RTOContent>
        <RTOLeft>
          <ImageContainer>
            <BackgroundImage 
              inView={inView} 
              aria-label="RTO Partner Qualification Certificate 1"
              role="img"
            />
            <GuaranteedImage 
              inView={inView} 
              aria-label="RTO Partner Qualification Certificate 2"
              role="img"
            />
            <CertificationBadge 
              src="/KD A1 Certified.webp"
              alt="Certified Badge"
              scrollProgress={scrollProgress}
              onError={(e) => {
                console.error('Failed to load certified badge image:', e);
                e.target.style.display = 'none';
              }}
              onLoad={() => console.log('Certified badge loaded successfully')}
            />
          </ImageContainer>
        </RTOLeft>

        <RTORight>
          <CertifiedBadge>
            <FiStar />
            Guaranteed & Certified
          </CertifiedBadge>
          
          <h2>All Qualifications Awarded By Our Partner RTOs</h2>
          
          <p>
            Your skills and career journey are unique — so your path to 
            getting recognised should be too. At RPL Support, we treat every 
            client's case with dedicated, personalised attention to help you fill 
            any knowledge gaps and achieve the qualifications you truly 
            deserve.
          </p>

          <p>
            We partner only with trusted <strong>Registered Training Organisations 
            (RTOs)</strong> to ensure every qualification you earn through RPL is 
            nationally recognised and meets Australian industry standards.
          </p>

          <p>
            Explore all the industries and qualifications available through our 
            partner RTOs. Get in touch today to start your Recognition of 
            Prior Learning (RPL) process and take your career to the next 
            level.
          </p>

          <ExploreButton to="/courses">
            Show & More →
          </ExploreButton>
        </RTORight>
      </RTOContent>
    </RTOContainer>
  );
};

export default RTOSection; 